import {
  Children,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type JSXElementConstructor
} from "react";

import type { TCellProps } from "../TCell/TCell";
import type { TColumnProps } from "../TColumn/TColumn";
import type {
  FilterTypes,
  FilterProps
} from "../TableFilters/TableFilters.type";
import {
  TBody,
  TCell,
  TFoot,
  THead,
  TRowExpanded,
  type TRowExpandedProps
} from "../..";

type TableColumn = {
  id: string;
  header: string;
  accessorKey: string;
  footer?: string;
  minSize?: number;
  enableSorting?: boolean;
  enableColumnFilter?: boolean;
  enableResizing?: boolean;
  cell?: (props: { row: any; getValue: () => any }) => React.ReactNode;
  meta?: {
    onSort?: (sortOrder?: "asc" | "desc") => void;
    onFilter?: (value: any) => void;
    filterProps?: FilterProps;
    filterType?: FilterTypes;
  };
};

/**
 * This function extracts the data from the childrens (THead, TColumn) of
 * the Table component and returns the data & columns.
 *
 * Make sure to memoize the return value of this function to avoid unnecessary
 * re-renders.
 *
 * @param children
 * @returns object with data and columns
 */
export const extractDataFromTableComponents = <
  TRowData extends Record<string, string>
>(
  children?: ReactElement[]
) => {
  let tableColumnHeader: ReactNode = null;
  let tableBody: ReactNode = null;
  let tableColumnFooter: ReactNode = null;

  Children.toArray(children).forEach(child => {
    if (isValidElement(child)) {
      const childType = child.type;
      if (childType === THead) {
        tableColumnHeader = child.props.children;
      } else if (childType === TBody) {
        tableBody = child.props.children;
      } else if (childType === TFoot) {
        tableColumnFooter = child.props.children;
      }
    }
    return child;
  });

  // To make sure this is equivalent to current table library's(tanstack/react-table) ColumnDef
  // that we are using in the Table component
  const columns: TableColumn[] = [];

  if (tableColumnHeader) {
    (
      Children.toArray(tableColumnHeader) as ReactElement<TColumnProps>[]
    ).forEach(headerColumn => {
      columns.push({
        id: headerColumn.props.id,
        accessorKey: headerColumn.props.id,
        header: headerColumn.props.children as string,
        cell: headerColumn.props.cell,
        minSize: headerColumn.props.width || 150,
        enableSorting: !!headerColumn.props.canSort,
        enableColumnFilter: !!headerColumn.props.canFilter,
        enableResizing: headerColumn.props.canResize,
        meta: {
          onSort: headerColumn.props.onSort,
          filterType: headerColumn.props.filterType,
          filterProps: headerColumn.props.filterProps,
          onFilter: headerColumn.props.onFilter
        }
      });
    });
  }

  if (tableColumnFooter) {
    (
      Children.toArray(tableColumnFooter) as ReactElement<TColumnProps>[]
    ).forEach(footerColumn => {
      const presentColumn = columns.find(column => {
        return column.id === footerColumn.props.id.toString();
      });
      if (presentColumn) {
        presentColumn.footer = footerColumn.props.children as string;
      }
    });
  }

  const data: TRowData[] = [];

  if (tableBody) {
    (Children.toArray(tableBody) as ReactElement[]).forEach(row => {
      const currentRow: Record<string, any> = { ...row.props.row };
      (
        Children.toArray(row.props.children) as ReactElement<
          TCellProps | TRowExpandedProps
        >[]
      ).forEach(cell => {
        const childType = cell.type as JSXElementConstructor<any>;
        currentRow.id = row.props.id;
        if (childType === TCell) {
          currentRow[(cell as ReactElement<TCellProps>).props.columnId] =
            typeof cell.props.children === "object"
              ? cell.props.children // JSX legacy support. TODO: Remove this once we migrate to the new cell renderer
              : cell.props.children?.toString();
        } else if (childType === TRowExpanded) {
          currentRow.subExpandedRow = [cell.props.children];
          currentRow.subExpandedRowLabel = (
            cell as ReactElement<TRowExpandedProps>
          ).props.label;
        }
      });
      data.push(currentRow as TRowData);
    });
  }

  return { data, columns };
};
